import { useId } from "react";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import "./CatalogItemClassification.css";
import { pendingReferenceLabel } from "./catalogReferenceOptions";

const messages = {
  en: { title: "Item classification", family: "Family", subfamily: "Subfamily", category: "Category", subcategory: "Subcategory", missing: "Not assigned", unavailable: "Not available", before: "Previous classification", proposed: "Proposed classification" },
  es: { title: "Clasificación del artículo", family: "Familia", subfamily: "Subfamilia", category: "Categoría", subcategory: "Subcategoría", missing: "Sin asignar", unavailable: "No disponible", before: "Clasificación anterior", proposed: "Clasificación propuesta" },
  ca: { title: "Classificació de l'article", family: "Família", subfamily: "Subfamília", category: "Categoria", subcategory: "Subcategoria", missing: "Sense assignar", unavailable: "No disponible", before: "Classificació anterior", proposed: "Classificació proposada" },
};
const levels = ["family", "subfamily", "category", "subcategory"];
const classificationFields = levels.map(level => `${level}_id`);
const referenceKey = value => value == null || value === "" ? null : String(value);
function hasClassificationChanges(request) {
  if (request.operation !== "update") return true;
  if (Array.isArray(request.changed_fields)) return request.changed_fields.some(key => classificationFields.includes(key));
  if (!request.payload || !request.previous_values) return true;
  return classificationFields.some(key => Object.hasOwn(request.payload, key) &&
    (!Object.hasOwn(request.previous_values, key) || referenceKey(request.payload[key]) !== referenceKey(request.previous_values[key])));
}
const fallbackPath = (values, labels) => Object.fromEntries(levels.map(level => {
  const id = values?.[`${level}_id`], name = labels?.[`${level}_id`];
  const unresolvedParent = ["family", "subfamily"].includes(level) && values?.category_id;
  return [level, id || name || unresolvedParent ? { name: typeof name === "string" ? name : null } : null];
}));

export default function CatalogItemClassification({ request }) {
  const { languageId } = usePmsTheme();
  const text = messages[languageId] || messages.en;
  const titleId = useId();
  const editing = request.operation === "update";
  const proposed = request.classification || fallbackPath(request.payload, request.payload_labels);
  const before = request.previous_classification || fallbackPath(request.previous_values, request.previous_values_labels);
  if (!hasClassificationChanges(request)) return null;
  const ambiguousReference = level => editing && proposed[level]?.name && proposed[level]?.name === before[level]?.name &&
    proposed[level]?.id != null && before[level]?.id != null && referenceKey(proposed[level].id) !== referenceKey(before[level].id);
  const renderPath = (path, label) => <div className="catalogItemClassification__path">
    {label && <h4>{label}</h4>}
    <dl>{levels.map(level => <div key={level}>
      <dt>{text[level]}</dt>
      <dd className={!path[level]?.name ? "catalogItemClassification__missing" : undefined}>
        {path[level]?.name || (path[level] ? text.unavailable : text.missing)}
        {ambiguousReference(level) && <small>#{path[level].id}</small>}
        {path[level]?.approval_pending && <small>{pendingReferenceLabel(languageId)}</small>}
      </dd>
    </div>)}</dl>
  </div>;
  return <section className="catalogItemClassification" aria-labelledby={titleId}>
    <h3 id={titleId}>{text.title}</h3>
    {editing && renderPath(before, text.before)}
    {renderPath(proposed, editing ? text.proposed : null)}
  </section>;
}
